/**
 * „Verfügbar bis" je Teamline — der Schritt vor dem Anlegen.
 *
 * 🔴 Matze, 21.09.2026: „Verfügbarkeit bis soll gesetzt werden. Muss bei der
 * Artikelanlage in Abhängigkeit der Teamline vom User angegeben werden." Eine
 * Squad-Linie läuft anders aus als eine Essential-Linie; ein Datum für den ganzen
 * Lauf wäre falsch, sobald eine Datei mehrere Linien enthält — und das ist der
 * Normalfall.
 *
 * 🔑 Gezeigt werden nur die Linien, die in DIESER Datei vorkommen, mit Artikelzahl.
 * erima führt 55 Linien; eine Liste davon wäre eine Zumutung und würde die drei, um
 * die es geht, unauffindbar machen.
 *
 * Ein leeres Feld heißt „kein Datum setzen" — nicht „Datum löschen". Der Unterschied
 * ist wichtig: `availableUntil` geht dann gar nicht mit.
 */
import { useCallback, useEffect, useState } from 'react';
import { CalendarClock, Save } from 'lucide-react';
import { api } from '@libero/core/services/api';
import LaufButton from './LaufButton';
import Schritt from './Schritt';
import { zahl } from '../lib/format';
import type { TeamlineAntwort } from '../lib/typen';

export default function TeamlinePanel(
  { entwurfId, schrittNummer }: { entwurfId: number; schrittNummer: number },
) {
  const [daten, setDaten] = useState<TeamlineAntwort | null>(null);
  const [werte, setWerte] = useState<Record<string, string>>({});
  const [laedt, setLaedt] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler(null);
    try {
      const antwort = await api.get<{ data: TeamlineAntwort }>(
        `/programmes/artikelimport/entwuerfe/${entwurfId}/teamlines`,
      );
      setDaten(antwort.data.data);
      setWerte(antwort.data.data.verfuegbarBis ?? {});
    } catch {
      setFehler('Die Teamlines ließen sich nicht laden.');
    } finally {
      setLaedt(false);
    }
  }, [entwurfId]);

  useEffect(() => { void laden(); }, [laden]);

  async function speichern() {
    setSpeichert(true);
    setFehler(null);
    setMeldung(null);
    try {
      const antwort = await api.patch<{ data: { verfuegbarBis: Record<string, string>; verworfen: string[] } }>(
        `/programmes/artikelimport/entwuerfe/${entwurfId}/verfuegbar-bis`,
        { verfuegbarBis: werte },
      );
      const { verfuegbarBis, verworfen } = antwort.data.data;
      setWerte(verfuegbarBis);
      setMeldung(
        verworfen.length > 0
          ? `Gespeichert. ${zahl(verworfen.length)} Angaben wurden verworfen, weil sie kein `
            + `gültiges Datum sind: ${verworfen.join(', ')}.`
          : 'Gespeichert.',
      );
    } catch {
      setFehler('Das Speichern ist fehlgeschlagen.');
    } finally {
      setSpeichert(false);
    }
  }

  const gesetzt = Object.values(werte).filter(w => String(w ?? '').trim().length > 0).length;
  const linien = daten?.teamlines ?? [];

  return (
    <Schritt
      nummer={schrittNummer}
      titel="Verfügbar bis (je Teamline)"
      zustand={linien.length === 0
        ? 'Noch keine Teamlines gelesen.'
        : gesetzt === 0
          ? `${zahl(linien.length)} Teamlines — noch kein Datum gesetzt, die Artikel laufen unbefristet.`
          : `${zahl(gesetzt)} von ${zahl(linien.length)} Teamlines mit Datum.`}
    >
      <p className="mb-3 text-sm text-[#1A1A1A]/70">
        {daten?.quelle === 'spalte'
          ? 'Die Teamline steht in der Quelldatei (Spalte „Linie").'
          : 'Die Teamline wird am Artikelnamen erkannt — über dieselben Regeln, die auch '
            + 'die Kollektions-Kategorie setzen.'}
        {' '}Ein leeres Feld heißt: kein Datum setzen, der Artikel läuft unbefristet.
      </p>

      {fehler && <p className="mb-2 text-sm text-red-700">{fehler}</p>}
      {meldung && <p className="mb-2 text-sm text-green-800">{meldung}</p>}

      {laedt && <p className="text-sm text-[#1A1A1A]/60">Lädt…</p>}

      {linien.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[#1A1A1A]/50">
              <th className="py-1 pr-3 font-medium">Teamline</th>
              <th className="py-1 pr-3 text-right font-medium">Artikel</th>
              <th className="py-1 pr-3 text-right font-medium">Varianten</th>
              <th className="py-1 pr-3 font-medium">Verfügbar bis</th>
              <th className="py-1 font-medium">Beispiele</th>
            </tr>
          </thead>
          <tbody>
            {linien.map(l => (
              <tr key={l.teamline} className="border-t border-[#DAD9D9]/50">
                <td className="py-1 pr-3 font-medium">{l.teamline}</td>
                <td className="py-1 pr-3 text-right tabular-nums">{zahl(l.artikel)}</td>
                <td className="py-1 pr-3 text-right tabular-nums">{zahl(l.varianten)}</td>
                <td className="py-1 pr-3">
                  <input
                    type="date"
                    value={werte[l.teamline] ?? ''}
                    onChange={e => setWerte(v => ({ ...v, [l.teamline]: e.target.value }))}
                    className="rounded border border-[#DAD9D9] px-2 py-1 text-sm"
                  />
                </td>
                <td className="py-1 text-xs text-[#1A1A1A]/50">
                  {l.beispiele.slice(0, 2).join(' · ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="mt-3 flex items-center gap-3">
        <LaufButton
          laeuft={speichert}
          laufText="Daten werden gespeichert"
          icon={<Save className="h-4 w-4" />}
          onClick={() => void speichern()}
          disabled={linien.length === 0}
          size="sm"
        >
          Daten speichern
        </LaufButton>
        <LaufButton
          laeuft={laedt}
          laufText="Teamlines werden gelesen"
          icon={<CalendarClock className="h-4 w-4" />}
          onClick={() => void laden()}
          variant="outline"
          size="sm"
        >
          Neu lesen
        </LaufButton>
      </div>
    </Schritt>
  );
}
